if (!customElements.get('vertical-scroll-milestones')) {
  class VerticalScrollMilestones extends HTMLElement {
    connectedCallback() {
      this.items = [...this.querySelectorAll('.vertical-milestones__item')];
      this.cards = [...this.querySelectorAll('.vertical-milestones__card')];
      this.nodes = [...this.querySelectorAll('.vertical-milestones__node')];
      this.mobileSteps = [...this.querySelectorAll('.vertical-milestones__mobile-step')];
      this.list = this.querySelector('.vertical-milestones__list');
      this.style.setProperty('--vm-mobile-count', this.items.length);
      this.activeIndex = -1;
      this.frame = null;
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      this.onScroll = this.onScroll.bind(this);
      this.onResize = this.onResize.bind(this);
      this.onContentLoad = this.onContentLoad.bind(this);
      this.activateFromClick = this.activateFromClick.bind(this);
      this.nodes.forEach((node) => node.addEventListener('click', this.activateFromClick));
      this.cards.forEach((card) => card.addEventListener('click', this.activateFromClick));
      this.mobileSteps.forEach((step) => step.addEventListener('click', this.activateFromClick));
      this.list?.addEventListener('scroll', this.onScroll, { passive: true });
      this.querySelectorAll('img').forEach((image) => image.addEventListener('load', this.onContentLoad));

      this.observer = new IntersectionObserver((entries) => {
        this.inView = entries[0]?.isIntersecting ?? false;
        if (this.inView) this.onScroll();
      }, { rootMargin: '20% 0px 20% 0px' });
      this.observer.observe(this);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize, { passive: true });
      this.syncDesktopLayout();
      document.fonts?.ready.then(() => this.syncDesktopLayout());
      this.update();
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onResize);
      this.nodes?.forEach((node) => node.removeEventListener('click', this.activateFromClick));
      this.cards?.forEach((card) => card.removeEventListener('click', this.activateFromClick));
      this.mobileSteps?.forEach((step) => step.removeEventListener('click', this.activateFromClick));
      this.list?.removeEventListener('scroll', this.onScroll);
      this.querySelectorAll('img').forEach((image) => image.removeEventListener('load', this.onContentLoad));
      if (this.resizeTimer) clearTimeout(this.resizeTimer);
      if (this.frame) cancelAnimationFrame(this.frame);
    }

    onResize() {
      if (this.resizeTimer) clearTimeout(this.resizeTimer);
      this.resizeTimer = setTimeout(() => {
        this.syncDesktopLayout();
        this.update();
      }, 80);
    }

    onContentLoad() {
      this.syncDesktopLayout();
      this.update();
    }

    syncDesktopLayout() {
      if (!this.cards.length || window.innerWidth <= 767) {
        this.style.removeProperty('--vm-card-height');
        return;
      }

      this.style.removeProperty('--vm-card-height');
      const tallestCard = Math.max(170, ...this.cards.map((card) => card.scrollHeight));
      const cardHeight = Math.ceil(tallestCard + 20);
      const stageHeight = Math.max(560, Math.ceil(((cardHeight + 32) * Math.max(1, this.items.length - 1)) / 1.68));
      this.style.setProperty('--vm-card-height', `${cardHeight}px`);
      this.style.setProperty('--vm-stage-height', `${stageHeight}px`);
    }
    onScroll() {
      if (!this.inView && window.innerWidth > 767) return;
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => { this.frame = null; this.update(); });
    }

    update() {
      if (!this.items.length) return;
      if (window.innerWidth <= 767) return this.updateMobile();
      const rect = this.getBoundingClientRect();
      const distance = Math.max(1, this.offsetHeight - window.innerHeight);
      const progress = Math.min(1, Math.max(0, -rect.top / distance));
      const exact = progress * (this.items.length - 1);
      const index = Math.min(this.items.length - 1, Math.max(0, Math.round(exact)));
      this.style.setProperty('--vm-progress', `${progress * 100}%`);
      this.style.setProperty('--vm-dot', `${(exact / Math.max(1, this.items.length - 1)) * 100}%`);
      this.setActive(index);
    }

    updateMobile() {
      const listRect = this.list.getBoundingClientRect();
      const target = listRect.left + listRect.width / 2;
      let closest = 0;
      let distance = Infinity;
      this.items.forEach((item, index) => {
        const rect = item.getBoundingClientRect();
        const current = Math.abs(rect.left + rect.width / 2 - target);
        if (current < distance) { closest = index; distance = current; }
      });
      this.setActive(closest);
    }

    setActive(index) {
      if (index === this.activeIndex) return;
      this.activeIndex = index;
      this.items.forEach((item, itemIndex) => {
        item.classList.toggle('is-active', itemIndex === index);
        item.classList.toggle('is-passed', itemIndex <= index);
      });
      this.mobileSteps.forEach((step, stepIndex) => {
        step.classList.toggle('is-active', stepIndex === index);
        step.classList.toggle('is-passed', stepIndex <= index);
      });
      this.style.setProperty('--vm-mobile-progress', `${index / Math.max(1, this.items.length - 1) * 100}%`);
    }

    activateFromClick(event) {
      const item = event.currentTarget.closest('.vertical-milestones__item');
      const index = Number(item?.dataset.index ?? event.currentTarget.dataset.index);
      if (!Number.isFinite(index)) return;
      if (window.innerWidth <= 767) {
        this.items[index]?.scrollIntoView({ behavior: this.reducedMotion ? 'auto' : 'smooth', block: 'nearest', inline: 'center' });
      } else {
        const distance = this.offsetHeight - window.innerHeight;
        const top = window.scrollY + this.getBoundingClientRect().top + distance * (index / Math.max(1, this.items.length - 1));
        window.scrollTo({ top, behavior: this.reducedMotion ? 'auto' : 'smooth' });
      }
      this.setActive(index);
    }
  }
  customElements.define('vertical-scroll-milestones', VerticalScrollMilestones);
}
